import { apiSlice, mockDataEnabled } from "./api.slice"
import { getBaseUrl, getMatrixApiConfig } from "@/utils/helpers/configs"
import { buildMemberOnlineContentFetchUrl } from "@/utils/helpers/contentSource"
import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "@/store"
import { InvestmentData } from "@/types/Investment"
import { SeminarsWebinarsData } from "@/types/SeminarsWebinars"
import { Message, ProductNumber } from "@/types/General"

const performanceChartDataUrl = getMatrixApiConfig("performanceChartDataUrl")
/*const planPerformanceChartDataUrl = getMatrixApiConfig(
  "planPerformanceChartDataUrl"
)*/

const planPerfChartDataIpfUrl = getMatrixApiConfig("planPerfChartDataIpfUrl")

type InvestmentDataResp = Omit<InvestmentData, "riskClass">
const matrixApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    perfChartData: builder.query({
      query: (unitCodes) => {
        if ("string" === typeof unitCodes) {
          return performanceChartDataUrl + `?unitCodes=${unitCodes}`
        }

        if (Array.isArray(unitCodes)) {
          return performanceChartDataUrl + `?unitCodes=${unitCodes.join(",")}`
        }

        return performanceChartDataUrl
      },
      // workaround for Matrix bug https://jira.squiz.net/browse/MATRIX-4456
      extraOptions: {
        maxRetries: 5,
      },
    }),
    planPerfChartData: builder.query({
      query: (unitCodes) => {
        return planPerfChartDataIpfUrl + `?unitCode=${unitCodes}`
      },
    }),
    bodyContent: builder.query<any, void>({
      query: () => ({
        url: buildMemberOnlineContentFetchUrl(
          getMatrixApiConfig("memberOnlineContentUrl"),
          "/_noproxycache"
        ),
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }),
    }),
    investmentData: builder.query<InvestmentDataResp[], void>({
      query: () => getMatrixApiConfig("investmentDataIpfUrl"),
    }),
    seminarsWebinarsData: builder.query<SeminarsWebinarsData, void>({
      query: () => getMatrixApiConfig("seminarsWebinarsUrl"),
    }),
    subpageContent: builder.query({
      query: (id) => ({
        url: `${getBaseUrl()}?a=${id}&SQ_ASSET_CONTENTS_RAW`,
        responseHandler: (resp) => resp.text(),
      }),
    }),
    messagesData: builder.query<
      Message[],
      { ids: ProductNumber[]; memberId?: string }
    >({
      query: (params) =>
        getMatrixApiConfig("memberOnlineMessagesUrl") +
        `?${new URLSearchParams(params as unknown as Record<string, string>)}`,
    }),
  }),
})

if (process.env.NODE_ENV === "development" && mockDataEnabled) {
  matrixApi.injectEndpoints(require("./mockApis")["mockMatrixApis"])
}

// Selectors
const matrixContent = matrixApi.endpoints.bodyContent.select()

export const bodyContent = createSelector(
  matrixContent,
  (result) => result?.data
)

const investments = matrixApi.endpoints.investmentData.select()

export const selectInvestments = createSelector(
  investments,
  (result) => result?.data ?? []
)

export const selectInvestmentsUnit = createSelector(
  selectInvestments,
  (_state: RootState, unitNumber: number) => unitNumber,
  (results, unitNumber) =>
    results.find((item) => +item.unitNumber === unitNumber)
)

export const selectInvestmentsCode = createSelector(
  selectInvestments,
  (_state: RootState, code: string) => code,
  (results, code) => results.find((item) => item.code === code)
)

export type MatrixApi = typeof matrixApi

export const {
  usePerfChartDataQuery,
  usePlanPerfChartDataQuery,
  useBodyContentQuery,
  useInvestmentDataQuery,
  useSeminarsWebinarsDataQuery,
  useSubpageContentQuery,
  useMessagesDataQuery,
} = matrixApi
